/**
 * ACM Platform Hub — přehled propojení všech Codable projektů: granty na
 * nástroje platformy a propojení mezi projekty (App hub). Správa jednoho
 * projektu je v jeho nastavení → Propojení (odkaz u každého projektu).
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, Share2 } from 'lucide-react';
import { Badge, Card, CardContent, EmptyState, PageHeader } from '../../components/ui';
import { api } from '../../lib/api';
import { useTranslation } from 'react-i18next';

interface Grant {
  project_slug: string;
  tool: string;
  kind: string;
  peer_slug: string | null;
  enabled: boolean;
  roles?: string[] | null;
  rate_limit?: { per_minute?: number; per_hour?: number; per_day?: number } | null;
  grantee_module?: string | null;
  grantee_module_name?: string | null;
}
interface ProjectRow {
  id: string;
  slug: string;
  name: string;
}

export default function PlatformHubPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['platform-hub', 'grants'],
    queryFn: () =>
      api.mcp<{ ok?: boolean; grants?: Grant[]; error?: string }>('platform-hub.grants', {}, {
        silent: true,
      }),
  });
  const projects = useQuery({
    queryKey: ['platform-hub', 'projects'],
    queryFn: () => api.mcp<{ projects?: ProjectRow[] }>('codable.project_list', {}, { silent: true }),
  });
  const bySlug = new Map((projects.data?.projects ?? []).map((p) => [p.slug, p]));

  const grants = data?.grants ?? [];
  // Grant modulu (volající modul → sdílená služba) má vlastní skupinu, ne „všechny projekty".
  const groupOf = (g: Grant) =>
    g.grantee_module ? `module:${g.grantee_module_name ?? g.grantee_module}` : g.project_slug;
  const limitText = (l: NonNullable<Grant['rate_limit']>) =>
    [
      l.per_minute ? t('hubConn.perMin', '{{n}}/min', { n: l.per_minute }) : null,
      l.per_hour ? t('hubConn.perHour', '{{n}}/h', { n: l.per_hour }) : null,
      l.per_day ? t('hubConn.perDay', '{{n}}/den', { n: l.per_day }) : null,
    ].filter(Boolean).join(', ');
  const groups = new Map<string, Grant[]>();
  for (const g of grants) groups.set(groupOf(g), [...(groups.get(groupOf(g)) ?? []), g]);

  return (
    <div className='space-y-6'>
      <PageHeader
        icon={Share2}
        title='Platform Hub'
        description={t(
          'hubConn.pageHint',
          'Kudy aplikace Codable projektů volají nástroje platformy a jiné projekty (/acm/call). Granty, klíč aplikace, vypínač a provoz se spravují u projektu: Nastavení projektu → Propojení.',
        )}
      />
      <Card>
        <CardContent className='p-4 space-y-4'>
          {isLoading && <p className='text-sm text-gray-500'>{t('platformhub.nacitamGranty', 'Načítám granty…')}</p>}
          {data?.ok === false && <p className='text-sm text-amber-400'>{data.error}</p>}
          {!isLoading && grants.length === 0 && (
            <EmptyState icon={Share2} title={t('platformhub.zadneGranty', 'Žádné granty')} description={t('platformhub.defaultMailSeNasadi', 'Default mail.* se nasadí při startu modulu.')} />
          )}
          {[...groups.entries()].map(([slug, list]) => {
            const p = bySlug.get(slug);
            return (
              <div key={slug} className='rounded-xl border border-gray-800 bg-gray-900/40 p-3'>
                <div className='mb-2 flex items-center gap-2'>
                  <span className='font-mono text-sm text-white'>{slug}</span>
                  {p && <span className='text-xs text-gray-500'>{p.name}</span>}
                  <div className='flex-1' />
                  {p
                    ? (
                      <Link to={`/modules/codable/${p.id}?settings=1`} className='text-xs text-plex hover:underline'>
                        {t('hubConn.manage', 'Spravovat v Propojení')} <ExternalLink size={10} className='inline' />
                      </Link>
                    )
                    : slug.startsWith('module:')
                    ? <Badge variant='purple'>{t('hubConn.moduleGrant', 'grant modulu')}</Badge>
                    : slug === '*'
                    ? <Badge variant='info'>{t('hubConn.allProjectsGrant', 'všechny projekty')}</Badge>
                    : null}
                </div>
                <div className='space-y-1'>
                  {list.map((g, i) => (
                    <div key={i} className='flex items-center gap-2 text-xs'>
                      <Badge variant={g.kind === 'peer' ? 'purple' : 'default'}>
                        {g.kind === 'peer' ? 'App hub' : t('hubConn.platform', 'platforma')}
                      </Badge>
                      <span className={`font-mono ${g.enabled ? 'text-gray-200' : 'text-gray-500 line-through'}`}>{g.tool}</span>
                      {g.peer_slug && <span className='text-gray-500'>→ {g.peer_slug}</span>}
                      {g.roles?.length ? <span className='text-gray-500'>({g.roles.join(', ')})</span> : null}
                      {g.rate_limit ? <Badge variant='default'>{limitText(g.rate_limit)}</Badge> : null}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
